import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Search, Plus, X, Check, Save, Sparkles, ChevronRight, ChevronDown,
  Flame, Beef, Fish, ChefHat, Cookie, Soup, CakeSlice, Leaf,
  Droplets, Wheat, Carrot, Globe2, ArrowRightLeft, Eye,
  UtensilsCrossed, GripVertical, AlertCircle
} from 'lucide-react'
import { useApp } from '../App'
import { dishesAPI, menusAPI, recipesAPI, componentsAPI } from '../lib/api'
import { useToastActions } from '../components/Toast'

// ══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ══════════════════════════════════════════════════════════════════════════════

const MEAL_SLOTS = [
  { id: 'chicken', label: 'Chicken', icon: Flame, color: 'emerald' },
  { id: 'chicken_2', label: 'Chicken 2', icon: Flame, color: 'emerald' },
  { id: 'beef', label: 'Beef', icon: Beef, color: 'red' },
  { id: 'seafood', label: 'Seafood', icon: Fish, color: 'cyan' },
  { id: 'seafood_2', label: 'Seafood 2', icon: Fish, color: 'teal' },
  { id: 'veg', label: 'Veg', icon: Leaf, color: 'lime' },
  { id: 'cheat', label: 'Cheat', icon: Cookie, color: 'amber' },
  { id: 'pasta', label: 'Pasta', icon: Cookie, color: 'stone' },
  { id: 'salad', label: 'Salad', icon: Leaf, color: 'emerald' },
  { id: 'salad_2', label: 'Salad 2', icon: Leaf, color: 'emerald' },
]

const SIDE_SLOTS = [
  { id: 'side-1', label: 'Broth', icon: Soup, color: 'orange', category: 'broth' },
  { id: 'side-2', label: 'Chia', icon: CakeSlice, color: 'pink', category: 'chia' },
  { id: 'side-3', label: 'Broth 2', icon: Soup, color: 'orange', category: 'broth_2' },
  { id: 'side-4', label: 'Oats', icon: Wheat, color: 'amber', category: 'oats' },
  { id: 'side-5', label: 'Chia 2', icon: CakeSlice, color: 'pink', category: 'chia_2' },
]

const ADDON_SLOTS = [
  { id: 'addon-1', label: 'Wellness Shots', icon: Droplets, color: 'emerald', category: 'wellness_shots' },
  { id: 'addon-2', label: 'Date Balls', icon: Cookie, color: 'amber', category: 'date_balls' },
]

const LIBRARY_TABS = [
  { id: 'dishes', label: 'Complete Dishes', icon: UtensilsCrossed },
  { id: 'proteins', label: 'Proteins', icon: Beef },
  { id: 'sauces', label: 'Sauces', icon: Droplets },
  { id: 'vegetables', label: 'Vegetables', icon: Carrot },
  { id: 'grains', label: 'Carbs', icon: Wheat },
  { id: 'bases', label: 'Broth & Chia', icon: Soup },
]

const COMPONENT_COLORS = {
  protein: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-700' },
  sauce: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-700' },
  vegetable: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', badge: 'bg-green-100 text-green-700' },
  carb: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700' },
  base: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700' },
}

const SLOT_COLORS = {
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-300', header: 'bg-emerald-600', text: 'text-emerald-700', light: 'bg-emerald-100' },
  red: { bg: 'bg-red-50', border: 'border-red-300', header: 'bg-red-600', text: 'text-red-700', light: 'bg-red-100' },
  cyan: { bg: 'bg-cyan-50', border: 'border-cyan-300', header: 'bg-cyan-600', text: 'text-cyan-700', light: 'bg-cyan-100' },
  teal: { bg: 'bg-teal-50', border: 'border-teal-300', header: 'bg-teal-700', text: 'text-teal-700', light: 'bg-teal-100' },
  lime: { bg: 'bg-lime-50', border: 'border-lime-300', header: 'bg-lime-600', text: 'text-lime-700', light: 'bg-lime-100' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-300', header: 'bg-amber-600', text: 'text-amber-700', light: 'bg-amber-100' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-300', header: 'bg-orange-600', text: 'text-orange-700', light: 'bg-orange-100' },
  pink: { bg: 'bg-pink-50', border: 'border-pink-300', header: 'bg-pink-600', text: 'text-pink-700', light: 'bg-pink-100' },
}

// ══════════════════════════════════════════════════════════════════════════════
// MACRO DISPLAY
// ══════════════════════════════════════════════════════════════════════════════

function MacroBar({ cal, p, f, c, fb, size = 'sm' }) {
  if (!cal && !p) return null
  const textSize = size === 'xs' ? 'text-[10px]' : 'text-xs'
  return (
    <div className={`flex items-center gap-1.5 ${textSize}`}>
      <span className="font-semibold text-havn-charcoal">{cal || 0}</span>
      <span className="text-havn-gray-400">cal</span>
      <span className="text-havn-gray-300">·</span>
      <span className="text-red-600">{p || 0}P</span>
      <span className="text-amber-600">{f || 0}F</span>
      <span className="text-blue-600">{c || 0}C</span>
      {fb > 0 && <span className="text-green-600">{fb}Fb</span>}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// WORKSPACE SLOT (meal or side)
// ══════════════════════════════════════════════════════════════════════════════

function WorkspaceSlot({ slot, meal, isActive, onClick, onClear, isSide }) {
  const colors = SLOT_COLORS[slot.color]
  const Icon = slot.icon
  const isEmpty = !meal || (!meal.name && !meal.protein)

  return (
    <div
      onClick={onClick}
      className={`rounded-xl border-2 transition-all cursor-pointer ${
        isActive
          ? `${colors.border} ${colors.bg} ring-2 ring-offset-1 ring-${slot.color}-400 shadow-md`
          : isEmpty
            ? 'border-dashed border-havn-cream-300 bg-white hover:border-havn-gray-300 hover:shadow-sm'
            : `${colors.border} bg-white hover:shadow-sm`
      }`}
    >
      {/* Header */}
      <div className={`px-3 py-2 flex items-center justify-between ${isEmpty ? '' : `${colors.light} rounded-t-[10px]`}`}>
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${isEmpty ? 'text-havn-gray-400' : colors.text}`} />
          <span className={`text-xs font-semibold uppercase tracking-wider ${isEmpty ? 'text-havn-gray-400' : colors.text}`}>
            {slot.label}
          </span>
        </div>
        {!isEmpty && (
          <button
            onClick={(e) => { e.stopPropagation(); onClear() }}
            className="p-0.5 text-havn-gray-400 hover:text-red-500 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="px-3 py-2">
        {isEmpty ? (
          <div className="flex items-center justify-center py-3 text-havn-gray-400">
            <Plus className="w-4 h-4 mr-1" />
            <span className="text-xs">
              {isSide ? 'Add side' : 'Add meal'}
            </span>
          </div>
        ) : isSide ? (
          // Side slot — just the dish name + macros
          <div>
            <p className="font-medium text-sm text-havn-charcoal truncate">{meal.name}</p>
            <MacroBar cal={meal.calories} p={meal.protein} f={meal.fat} c={meal.carbs} fb={meal.fiber} size="xs" />
          </div>
        ) : (
          // Meal slot — show assembled components
          <div className="space-y-1">
            {meal.name && (
              <p className="font-display font-semibold text-sm text-havn-charcoal truncate mb-1.5">
                {meal.name}
              </p>
            )}
            <ComponentSlotRow label="Protein" value={meal.protein_name} type="protein" />
            <ComponentSlotRow label="Sauce" value={meal.sauce_name} type="sauce" />
            <ComponentSlotRow label="Veg 1" value={meal.veg_name} type="vegetable" />
            <ComponentSlotRow label="Veg 2" value={meal.veg2_name} type="vegetable" />
            <ComponentSlotRow label="Carb" value={meal.carb_name} type="carb" />
            {(meal.calories || meal.total_cal) && (
              <div className="pt-1 mt-1 border-t border-havn-cream-200">
                <MacroBar
                  cal={meal.calories || meal.total_cal}
                  p={meal.protein_g || meal.protein}
                  f={meal.fat}
                  c={meal.carbs}
                  fb={meal.fiber}
                  size="xs"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function ComponentSlotRow({ label, value, type }) {
  const compColor = COMPONENT_COLORS[type] || COMPONENT_COLORS.base
  return (
    <div className="flex items-center gap-1.5 text-xs">
      <span className={`w-14 px-1 py-0.5 rounded text-center font-medium ${compColor.badge}`}>
        {label}
      </span>
      <span className={`flex-1 truncate ${value ? 'text-havn-charcoal' : 'text-havn-gray-400 italic'}`}>
        {value || 'Empty'}
      </span>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// RECIPE CARD (for browsing individual recipes in library)
// ══════════════════════════════════════════════════════════════════════════════

function RecipeCard({ recipe, onAdd, onViewDetail }) {
  const categoryType = recipe.category === 'protein' ? 'protein'
    : recipe.category === 'sauce' ? 'sauce'
    : recipe.category === 'vegetable' ? 'vegetable'
    : recipe.category === 'grain' ? 'carb'
    : 'base'
  const compColor = COMPONENT_COLORS[categoryType] || COMPONENT_COLORS.base

  return (
    <div className={`bg-white rounded-lg border border-havn-cream-300 hover:shadow-md transition-all overflow-hidden`}>
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-1">
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${compColor.badge}`}>
                {recipe.category}
              </span>
              {recipe.allergens && recipe.allergens.length > 0 && (
                <span className="text-[10px] text-orange-500 flex items-center gap-0.5">
                  <AlertCircle className="w-3 h-3" />
                  {recipe.allergens.length}
                </span>
              )}
            </div>
            <h4 className="font-semibold text-sm text-havn-charcoal leading-tight">
              {recipe.name}
            </h4>
            <MacroBar cal={recipe.macros?.cal} p={recipe.macros?.p} f={recipe.macros?.f} c={recipe.macros?.c} fb={recipe.macros?.fb} size="xs" />
          </div>
          <div className="flex flex-col gap-1">
            <button
              onClick={(e) => { e.stopPropagation(); onAdd(recipe) }}
              className="p-1.5 rounded-lg bg-havn-green-50 text-havn-green-600 hover:bg-havn-green-100 transition-colors"
              title="Add to active slot"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onViewDetail(recipe) }}
              className="p-1.5 rounded-lg bg-havn-cream-100 text-havn-gray-500 hover:bg-havn-cream-200 transition-colors"
              title="View recipe details"
            >
              <Eye className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        {recipe.used_in_dishes && recipe.used_in_dishes.length > 0 && (
          <p className="text-[10px] text-havn-gray-400 mt-1.5 truncate">
            Used in: {recipe.used_in_dishes.slice(0, 2).map(d => typeof d === 'string' ? d : d.name).join(', ')}
            {recipe.used_in_dishes.length > 2 && ` +${recipe.used_in_dishes.length - 2}`}
          </p>
        )}
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// DISH CARD (for complete dishes in library)
// ══════════════════════════════════════════════════════════════════════════════

function DishCard({ dish, onAdd, onViewDetail }) {
  const catColors = {
    chicken: 'bg-emerald-100 text-emerald-700',
    beef: 'bg-red-100 text-red-700',
    seafood: 'bg-cyan-100 text-cyan-700',
    chef: 'bg-purple-100 text-purple-700',
    cheat: 'bg-amber-100 text-amber-700',
    broth: 'bg-orange-100 text-orange-700',
    chia: 'bg-pink-100 text-pink-700',
  }

  return (
    <div className="bg-white rounded-lg border border-havn-cream-300 hover:shadow-md transition-all overflow-hidden">
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-1">
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${catColors[dish.category] || 'bg-gray-100 text-gray-700'}`}>
                {dish.category}
              </span>
              {dish.cuisine && (
                <span className="flex items-center gap-0.5 text-[10px] text-havn-gray-400">
                  <Globe2 className="w-3 h-3" /> {dish.cuisine}
                </span>
              )}
            </div>
            <h4 className="font-semibold text-sm text-havn-charcoal leading-tight">
              {dish.name}
            </h4>
            <MacroBar cal={dish.calories} p={dish.protein} f={dish.fat} c={dish.carbs} fb={dish.fiber} size="xs" />
          </div>
          <div className="flex flex-col gap-1">
            <button
              onClick={(e) => { e.stopPropagation(); onAdd(dish) }}
              className="p-1.5 rounded-lg bg-havn-green-50 text-havn-green-600 hover:bg-havn-green-100 transition-colors"
              title="Add to workspace"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onViewDetail(dish) }}
              className="p-1.5 rounded-lg bg-havn-cream-100 text-havn-gray-500 hover:bg-havn-cream-200 transition-colors"
              title="View details"
            >
              <Eye className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Inline components preview */}
        {dish.components && dish.components.length > 0 && (
          <div className="mt-2 space-y-0.5">
            {dish.components.slice(0, 4).map((comp, i) => (
              <div key={i} className="flex items-center gap-1.5 text-[10px]">
                <span className={`px-1 py-0.5 rounded font-medium ${COMPONENT_COLORS[comp.component_type || comp.type]?.badge || 'bg-gray-100 text-gray-600'}`}>
                  {(comp.component_type || comp.type || '').slice(0, 4)}
                </span>
                <span className="text-havn-charcoal truncate">{comp.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// RECIPE DETAIL DRAWER (slide-in from right)
// ══════════════════════════════════════════════════════════════════════════════

function RecipeDetailDrawer({ recipe, onClose, onAdd }) {
  if (!recipe) return null

  const categoryType = recipe.category === 'protein' ? 'protein'
    : recipe.category === 'sauce' ? 'sauce'
    : recipe.category === 'vegetable' ? 'vegetable'
    : recipe.category === 'grain' ? 'carb'
    : 'base'
  const compColor = COMPONENT_COLORS[categoryType] || COMPONENT_COLORS.base

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div
        className="relative w-full max-w-md bg-white shadow-xl overflow-y-auto animate-slide-in-right"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-havn-cream-200 px-5 py-4 z-10">
          <div className="flex items-start justify-between">
            <div>
              <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${compColor.badge} mb-1.5`}>
                {recipe.category}
              </span>
              <h2 className="font-display font-bold text-lg text-havn-charcoal">{recipe.name}</h2>
              <MacroBar cal={recipe.macros?.cal} p={recipe.macros?.p} f={recipe.macros?.f} c={recipe.macros?.c} fb={recipe.macros?.fb} />
            </div>
            <button onClick={onClose} className="p-1.5 text-havn-gray-400 hover:text-havn-charcoal rounded-lg hover:bg-havn-cream-100">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {/* Chef Notes */}
          {recipe.chef_notes && (
            <div className="p-3 bg-havn-brass-50 border border-havn-brass-200 rounded-lg">
              <h4 className="text-xs font-semibold text-havn-brass-600 uppercase tracking-wider mb-1">Chef Notes</h4>
              <p className="text-sm text-havn-charcoal">{recipe.chef_notes}</p>
            </div>
          )}

          {/* Allergens */}
          {recipe.allergens && recipe.allergens.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-havn-gray-500 uppercase tracking-wider mb-2">Allergens</h4>
              <div className="flex flex-wrap gap-1.5">
                {recipe.allergens.map(a => (
                  <span key={a} className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">{a}</span>
                ))}
              </div>
            </div>
          )}

          {/* Used In Dishes */}
          {recipe.used_in_dishes && recipe.used_in_dishes.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-havn-gray-500 uppercase tracking-wider mb-2">
                Used in {recipe.used_in_dishes.length} dishes
              </h4>
              <div className="space-y-1">
                {recipe.used_in_dishes.map((d, i) => (
                  <div key={i} className="px-2 py-1.5 bg-havn-cream-50 rounded text-xs text-havn-charcoal">
                    {typeof d === 'string' ? d : d.name}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Batch Size */}
          {recipe.batch_size && (
            <div className="p-3 bg-havn-cream-50 rounded-lg">
              <span className="text-xs text-havn-gray-500">Batch Size</span>
              <p className="font-semibold text-havn-charcoal">{recipe.batch_size} {recipe.batch_unit || ''}</p>
            </div>
          )}
        </div>

        {/* Footer — Add button */}
        <div className="sticky bottom-0 bg-white border-t border-havn-cream-200 p-4">
          <button
            onClick={() => { onAdd(recipe); onClose() }}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-havn-green-600 text-white rounded-lg hover:bg-havn-green-700 transition-colors text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Add to Active Slot
          </button>
        </div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// DISH DETAIL DRAWER (slide-in from right — for complete dishes)
// ══════════════════════════════════════════════════════════════════════════════

function DishDetailDrawer({ dish, onClose, onAdd, swapOptions }) {
  const [swappingType, setSwappingType] = useState(null) // which component type is being swapped
  const [swapSearch, setSwapSearch] = useState('')
  const [localComponents, setLocalComponents] = useState(dish?.components || [])

  // Reset local components when dish changes
  useEffect(() => {
    if (dish) setLocalComponents(dish.components || [])
  }, [dish])

  if (!dish) return null

  // Get swap list for the current swapping type
  const typeToSwapKey = { protein: 'proteins', sauce: 'sauces', vegetable: 'vegetables', carb: 'grains' }
  const swapList = swappingType ? (swapOptions?.[typeToSwapKey[swappingType]] || []) : []
  const filteredSwaps = swapSearch
    ? swapList.filter(s => s.name.toLowerCase().includes(swapSearch.toLowerCase()))
    : swapList

  function handleSwap(compIndex, newItem) {
    setLocalComponents(prev => {
      const updated = [...prev]
      updated[compIndex] = { ...updated[compIndex], name: newItem.name }
      return updated
    })
    setSwappingType(null)
    setSwapSearch('')
  }

  // Build a modified dish with swapped components + customizations JSON for the "Add" action
  function getModifiedDish() {
    const originalComps = dish.components || []
    const swaps = {}

    localComponents.forEach((comp, i) => {
      if (i < originalComps.length && comp.name !== originalComps[i].name) {
        const compType = comp.component_type || comp.type
        // Count how many of this type came before this index
        const typeIndex = localComponents.slice(0, i).filter(
          c => (c.component_type || c.type) === compType
        ).length
        swaps[`${compType}_${typeIndex}`] = {
          original: originalComps[i].name,
          new: comp.name
        }
      }
    })

    const customizations = Object.keys(swaps).length > 0
      ? JSON.stringify({ swaps })
      : null

    return { ...dish, components: localComponents, customizations }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div
        className="relative w-full max-w-md bg-white shadow-xl overflow-y-auto animate-slide-in-right"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-havn-cream-200 px-5 py-4 z-10">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="px-2 py-0.5 rounded text-xs font-semibold bg-havn-cream-200 text-havn-gray-600 capitalize">
                  {dish.category}
                </span>
                {dish.cuisine && (
                  <span className="flex items-center gap-1 text-xs text-havn-gray-400">
                    <Globe2 className="w-3 h-3" /> {dish.cuisine}
                  </span>
                )}
              </div>
              <h2 className="font-display font-bold text-lg text-havn-charcoal">{dish.name}</h2>
              <MacroBar cal={dish.calories} p={dish.protein} f={dish.fat} c={dish.carbs} fb={dish.fiber} />
            </div>
            <button onClick={onClose} className="p-1.5 text-havn-gray-400 hover:text-havn-charcoal rounded-lg hover:bg-havn-cream-100">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {/* Components with Swap */}
          {localComponents.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-havn-gray-500 uppercase tracking-wider mb-2">
                Components
                <span className="ml-2 font-normal normal-case text-havn-gray-400">tap swap to change</span>
              </h4>
              <div className="space-y-2">
                {localComponents.map((comp, i) => {
                  const type = comp.component_type || comp.type
                  const cc = COMPONENT_COLORS[type] || COMPONENT_COLORS.base
                  const canSwap = ['protein', 'sauce', 'vegetable', 'carb'].includes(type)
                  return (
                    <div key={i} className={`flex items-center gap-2 p-2.5 rounded-lg ${cc.bg} border ${cc.border}`}>
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${cc.badge} shrink-0`}>{type}</span>
                      <span className="flex-1 text-sm font-medium text-havn-charcoal truncate">{comp.name}</span>
                      {comp.portion_oz && <span className="text-xs text-havn-gray-400 shrink-0">{comp.portion_oz}oz</span>}
                      {canSwap && (
                        <button
                          onClick={() => { setSwappingType(type); setSwapSearch('') }}
                          className="flex items-center gap-1 px-2 py-1 rounded-md bg-white/80 border border-havn-gray-200 text-xs text-havn-gray-500 hover:text-havn-green-700 hover:border-havn-green-300 transition-colors shrink-0"
                        >
                          <ArrowRightLeft className="w-3 h-3" />
                          Swap
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Swap Picker (inline, shows when a swap button is tapped) */}
          {swappingType && (
            <div className="p-4 bg-havn-green-50 border border-havn-green-200 rounded-xl">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-semibold text-havn-green-700 uppercase tracking-wider">
                  Swap {swappingType}
                </h4>
                <button
                  onClick={() => { setSwappingType(null); setSwapSearch('') }}
                  className="p-1 text-havn-gray-400 hover:text-havn-charcoal"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="relative mb-3">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-havn-gray-400" />
                <input
                  type="text"
                  value={swapSearch}
                  onChange={e => setSwapSearch(e.target.value)}
                  placeholder={`Search ${swappingType}s...`}
                  className="w-full pl-8 pr-3 py-2 bg-white border border-havn-cream-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-havn-green-500"
                  autoFocus
                />
              </div>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {filteredSwaps.length === 0 ? (
                  <p className="text-xs text-havn-gray-400 text-center py-3">No options found</p>
                ) : (
                  filteredSwaps.map((item, idx) => {
                    // Find the component index to swap
                    const compIdx = localComponents.findIndex(c =>
                      (c.component_type || c.type) === swappingType
                    )
                    return (
                      <button
                        key={idx}
                        onClick={() => handleSwap(compIdx, item)}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-white text-left transition-colors group"
                      >
                        <span className="text-sm font-medium text-havn-charcoal group-hover:text-havn-green-700">{item.name}</span>
                        <div className="flex items-center gap-2 text-[10px] text-havn-gray-400">
                          <span>{item.cal}cal</span>
                          <span className="text-red-500">{item.p}P</span>
                          <span className="text-amber-500">{item.f}F</span>
                          <span className="text-blue-500">{item.c}C</span>
                        </div>
                      </button>
                    )
                  })
                )}
              </div>
            </div>
          )}

          {/* Chef Notes */}
          {dish.chef_notes && (
            <div className="p-3 bg-havn-brass-50 border border-havn-brass-200 rounded-lg">
              <h4 className="text-xs font-semibold text-havn-brass-600 uppercase tracking-wider mb-1">Chef Notes</h4>
              <p className="text-sm text-havn-charcoal">{dish.chef_notes}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-havn-cream-200 p-4">
          <button
            onClick={() => { onAdd(getModifiedDish()); onClose() }}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-havn-green-600 text-white rounded-lg hover:bg-havn-green-700 transition-colors text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Add All Components to Slot
          </button>
        </div>
      </div>
    </div>
  )
}


// ══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════════

export default function MenuDevelopment() {
  const { currentCity, currentMenu, setCurrentMenu, menus } = useApp()
  const { success, error } = useToastActions()

  // Data
  const [allDishes, setAllDishes] = useState([])
  const [recipeBrowser, setRecipeBrowser] = useState(null) // { grouped, counts }
  const [swapOptions, setSwapOptions] = useState(null) // { proteins, vegetables, grains, sauces }
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false) // flash "Saved!" on button after success

  // Workspace — 10 meal slots (incl. optional chicken_2 + seafood_2 + pasta + salad/salad_2) + 2 side slots
  const [workspace, setWorkspace] = useState({
    chicken: null, chicken_2: null, beef: null, seafood: null, seafood_2: null, veg: null, cheat: null, pasta: null, salad: null, salad_2: null,
    'side-1': null, 'side-2': null,
  })
  const [activeSlot, setActiveSlot] = useState(null) // which slot is selected for adding

  // Library
  const [libraryTab, setLibraryTab] = useState('dishes')
  const [searchQuery, setSearchQuery] = useState('')

  // Drawers
  const [detailRecipe, setDetailRecipe] = useState(null)
  const [detailDish, setDetailDish] = useState(null)

  // ── Load data ──────────────────────────────────────────────────────────────
  useEffect(() => {
    async function loadData() {
      setLoading(true)
      try {
        const [dishes, browser, swaps] = await Promise.all([
          dishesAPI.list({ city_id: currentCity }),
          recipesAPI.browser(currentCity),
          componentsAPI.getSwapPools().catch(() => null),
        ])
        setAllDishes(Array.isArray(dishes) ? dishes : [])
        setRecipeBrowser(browser)
        if (swaps) setSwapOptions(swaps)

        // Load existing menu into workspace
        if (currentMenu) {
          try {
            const menuData = await menusAPI.get(currentMenu)
            if (menuData?.dishes) {
              const ws = { chicken: null, chicken_2: null, beef: null, seafood: null, seafood_2: null, veg: null, cheat: null, pasta: null, salad: null, salad_2: null, 'side-1': null, 'side-2': null }
              menuData.dishes.forEach(d => {
                const meal = dishToWorkspaceMeal(d)
                if (['broth', 'chia'].includes(d.category)) {
                  // Place broth in side-1, chia in side-2
                  if (d.category === 'broth' && !ws['side-1']) {
                    ws['side-1'] = meal
                  } else if (d.category === 'chia' && !ws['side-2']) {
                    ws['side-2'] = meal
                  }
                } else {
                  const slotKey = d.category === 'chef' ? 'cheat' : d.category
                  if (ws[slotKey] === null) ws[slotKey] = meal
                  // If slot already taken, try to find an empty meal slot
                  else {
                    const empty = ['chicken', 'chicken_2', 'beef', 'seafood', 'seafood_2', 'veg', 'cheat', 'pasta', 'salad', 'salad_2'].find(k => ws[k] === null)
                    if (empty) ws[empty] = meal
                  }
                }
              })
              setWorkspace(ws)
            }
          } catch { /* menu might not exist */ }
        }
      } catch (err) {
        error('Failed to load data')
        console.error(err)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [currentCity, currentMenu])

  // ── Convert dish API data → workspace meal format ──────────────────────────
  function dishToWorkspaceMeal(d) {
    const comps = d.components || []
    const proteinComp = comps.find(c => (c.component_type || c.type) === 'protein')
    const sauceComp = comps.find(c => (c.component_type || c.type) === 'sauce')
    const vegComps = comps.filter(c => (c.component_type || c.type) === 'vegetable')
    const carbComp = comps.find(c => (c.component_type || c.type) === 'carb')

    return {
      dish_id: d.dish_id || d.id,
      name: d.name,
      category: d.category,
      cuisine: d.cuisine,
      calories: d.calories,
      protein: d.protein,
      protein_g: d.protein,
      fat: d.fat,
      carbs: d.carbs,
      fiber: d.fiber,
      chef_notes: d.chef_notes,
      components: comps,
      customizations: d.customizations || null,
      protein_name: proteinComp?.name || null,
      sauce_name: sauceComp?.name || null,
      veg_name: vegComps[0]?.name || null,
      veg2_name: vegComps[1]?.name || null,
      carb_name: carbComp?.name || null,
    }
  }

  // ── Add a complete dish to an appropriate workspace slot ────────────────────
  const addDishToSlot = useCallback((dish) => {
    const meal = dishToWorkspaceMeal(dish)
    const isSide = ['broth', 'chia'].includes(dish.category)

    setWorkspace(prev => {
      const next = { ...prev }

      if (isSide) {
        // Put in activeSlot if it's a side slot, otherwise match category slot
        const categorySlot = dish.category === 'broth' ? 'side-1' : 'side-2'
        const target = activeSlot && activeSlot.startsWith('side-') ? activeSlot
          : !next[categorySlot] ? categorySlot
          : ['side-1', 'side-2'].find(k => !next[k])
        if (target) next[target] = meal
        else { error('All side slots are full'); return prev }
      } else {
        // Put in activeSlot if it's a meal slot, otherwise match category, otherwise first empty
        const target = activeSlot && !activeSlot.startsWith('side-') ? activeSlot
          : (dish.category === 'chef' ? 'cheat' : dish.category) in next && !next[dish.category === 'chef' ? 'cheat' : dish.category]
            ? (dish.category === 'chef' ? 'cheat' : dish.category)
            : ['chicken', 'chicken_2', 'beef', 'seafood', 'seafood_2', 'veg', 'cheat', 'pasta', 'salad', 'salad_2'].find(k => !next[k])
        if (target) next[target] = meal
        else { error('All meal slots are full'); return prev }
      }

      return next
    })
    success(`Added ${dish.name}`)
  }, [activeSlot, success, error])

  // ── Add a base recipe (broth/chia) to the appropriate side slot ────────────
  const addBaseRecipeToSlot = useCallback((recipe) => {
    const macros = recipe.macros || {}
    const isBroth = recipe.name.toLowerCase().includes('broth')
    const categoryLabel = isBroth ? 'broth' : 'chia'
    const defaultSlot = isBroth ? 'side-1' : 'side-2'

    // Determine target slot
    const target = (activeSlot && activeSlot.startsWith('side-')) ? activeSlot
      : !workspace[defaultSlot] ? defaultSlot
      : ['side-1', 'side-2'].find(k => !workspace[k])

    if (!target) {
      error('All side slots are full')
      return
    }

    // Try to find a matching dish from allDishes (to preserve dish_id and avoid duplicates on save)
    const matchingDish = allDishes.find(d =>
      d.category === categoryLabel &&
      d.name.toLowerCase().includes(recipe.name.toLowerCase().split(' - ')[0].replace('chia pudding', '').trim() || 'chia')
    )

    if (matchingDish) {
      // Use the full dish (has dish_id, components, macros)
      const meal = dishToWorkspaceMeal(matchingDish)
      setWorkspace(prev => ({ ...prev, [target]: meal }))
    } else {
      // Fallback: create a lightweight meal from the recipe data
      const meal = {
        name: recipe.name,
        category: categoryLabel,
        calories: macros.cal || 0,
        protein: macros.p || 0,
        protein_g: macros.p || 0,
        fat: macros.f || 0,
        carbs: macros.c || 0,
        fiber: macros.fb || 0,
        components: [],
      }
      setWorkspace(prev => ({ ...prev, [target]: meal }))
    }

    const slotLabel = SIDE_SLOTS.find(s => s.id === target)?.label || target
    success(`Added ${recipe.name} to ${slotLabel} slot`)
  }, [activeSlot, workspace, allDishes, success, error])

  // ── Add an individual recipe component to the active slot ──────────────────
  const addRecipeToSlot = useCallback((recipe) => {
    // Base recipes (broth/chia) go to side slots, not meal component slots
    if (recipe.category === 'base') {
      addBaseRecipeToSlot(recipe)
      return
    }

    if (!activeSlot) {
      // Auto-select first empty meal slot
      const emptyMeal = ['chicken', 'chicken_2', 'beef', 'seafood', 'seafood_2', 'veg', 'cheat', 'pasta', 'salad', 'salad_2'].find(k => !workspace[k])
      if (emptyMeal) {
        setActiveSlot(emptyMeal)
        // Delay slightly so state updates
        setTimeout(() => addRecipeToSlotInner(recipe, emptyMeal), 0)
        return
      }
      error('Select a meal slot first')
      return
    }
    addRecipeToSlotInner(recipe, activeSlot)
  }, [activeSlot, workspace, error, addBaseRecipeToSlot])

  function addRecipeToSlotInner(recipe, slotKey) {
    const cat = recipe.category // protein, sauce, vegetable, grain
    const macros = recipe.macros || {}

    setWorkspace(prev => {
      const next = { ...prev }
      const current = next[slotKey] || {
        name: null, category: null, calories: 0, protein: 0, protein_g: 0,
        fat: 0, carbs: 0, fiber: 0, components: [],
        protein_name: null, sauce_name: null, veg_name: null, veg2_name: null, carb_name: null,
      }

      const updated = { ...current }

      if (cat === 'protein') {
        updated.protein_name = recipe.name
        updated.name = recipe.name // name the meal after the protein
      } else if (cat === 'sauce') {
        updated.sauce_name = recipe.name
      } else if (cat === 'vegetable') {
        // Fill veg_name first, then veg2_name
        if (!updated.veg_name) {
          updated.veg_name = recipe.name
        } else {
          updated.veg2_name = recipe.name
        }
      } else if (cat === 'grain') {
        updated.carb_name = recipe.name
      }

      // Accumulate macros (rough estimate)
      updated.calories = (updated.calories || 0) + (macros.cal || 0)
      updated.protein = (updated.protein || 0) + (macros.p || 0)
      updated.protein_g = updated.protein
      updated.fat = (updated.fat || 0) + (macros.f || 0)
      updated.carbs = (updated.carbs || 0) + (macros.c || 0)
      updated.fiber = (updated.fiber || 0) + (macros.fb || 0)

      next[slotKey] = updated
      return next
    })
    success(`Added ${recipe.name} to ${slotKey} slot`)
  }

  // ── Save workspace to menu API ─────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    // Check we have at least one item to save
    const filledSlots = Object.entries(workspace).filter(([_, meal]) => meal)
    if (filledSlots.length === 0) {
      error('Add at least one dish before saving.')
      return
    }

    setSaving(true)
    try {
      let menuId = currentMenu

      // Auto-create a menu if none selected
      if (!menuId) {
        const today = new Date()
        const menuName = `Week of ${today.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
        const newMenu = await menusAPI.create(currentCity, menuName)
        menuId = newMenu.id
        setCurrentMenu(menuId)
        localStorage.setItem('havn_current_menu', menuId.toString())
      }

      // For slots without a dish_id (user-assembled from individual components),
      // create the dish on-the-fly. Backend uses find-or-create to avoid duplicates.
      const dishEntries = []
      for (const [slotKey, meal] of filledSlots) {
        let dishId = meal.dish_id

        // Determine the correct category from the slot key
        const slotCategory = ['chicken', 'chicken_2', 'beef', 'seafood', 'seafood_2', 'veg', 'cheat', 'pasta', 'salad', 'salad_2'].includes(slotKey)
          ? slotKey
          : slotKey === 'side-1' ? 'broth'
          : slotKey === 'side-2' ? 'chia'
          : meal.category || 'chicken'

        if (!dishId) {
          // Dish name = protein name (the primary identity of the meal)
          // Falls back through other components if no protein is set
          const dishName = meal.protein_name
            || meal.sauce_name
            || meal.veg_name
            || meal.veg2_name
            || meal.carb_name
            || meal.name
            || `Custom ${slotKey}`

          // Build component objects for the backend
          const components = []
          if (meal.protein_name) components.push({ type: 'protein', name: meal.protein_name })
          if (meal.sauce_name) components.push({ type: 'sauce', name: meal.sauce_name })
          if (meal.veg_name) components.push({ type: 'vegetable', name: meal.veg_name })
          if (meal.veg2_name) components.push({ type: 'vegetable', name: meal.veg2_name })
          if (meal.carb_name) components.push({ type: 'carb', name: meal.carb_name })

          // Create the dish (find-or-create handles duplicates)
          const created = await dishesAPI.create({
            city_id: currentCity,
            name: dishName,
            category: slotCategory,
            calories: meal.calories || null,
            protein: meal.protein || null,
            fat: meal.fat || null,
            carbs: meal.carbs || null,
            fiber: meal.fiber || null,
            components,
          })
          dishId = created.id
        } else if (meal.category !== slotCategory) {
          // Existing dish placed in a different slot — sync category to match
          // This prevents stale categories from causing wrong Airtable import mapping
          try {
            await dishesAPI.update(dishId, { category: slotCategory })
          } catch (e) {
            console.warn(`Failed to update category for dish ${dishId}:`, e)
          }
        }

        dishEntries.push({
          dish_id: dishId,
          customizations: meal.customizations || null,
        })
      }

      // Deduplicate by dish_id (keep last entry's customizations)
      const seen = new Map()
      for (const entry of dishEntries) {
        seen.set(entry.dish_id, entry)
      }
      const uniqueEntries = [...seen.values()]

      try {
        await menusAPI.syncDishesWithCustomizations(menuId, uniqueEntries)
      } catch (syncErr) {
        // If menu was deleted (stale ID), create a fresh menu and retry
        if (syncErr.message && syncErr.message.includes('404')) {
          console.warn('Menu not found (stale ID), creating fresh menu...')
          const today = new Date()
          const menuName = `Week of ${today.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
          const newMenu = await menusAPI.create(currentCity, menuName)
          menuId = newMenu.id
          setCurrentMenu(menuId)
          localStorage.setItem('havn_current_menu', menuId.toString())
          await menusAPI.syncDishesWithCustomizations(menuId, uniqueEntries)
        } else {
          throw syncErr
        }
      }

      success(`Menu saved — ${uniqueEntries.length} dishes`)
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } catch (err) {
      console.error('Save failed:', err)
      error('Failed to save: ' + (err.message || 'Unknown error — check browser console'))
    } finally {
      setSaving(false)
    }
  }, [currentMenu, currentCity, workspace, success, error, setCurrentMenu])

  // ── Filter library items ───────────────────────────────────────────────────
  const filteredLibrary = useMemo(() => {
    const q = searchQuery.toLowerCase()

    if (libraryTab === 'dishes') {
      let dishes = [...allDishes]
      if (q) {
        dishes = dishes.filter(d =>
          d.name.toLowerCase().includes(q) ||
          (d.cuisine || '').toLowerCase().includes(q) ||
          (d.category || '').toLowerCase().includes(q)
        )
      }
      return dishes
    }

    // Recipe tabs
    const recipes = recipeBrowser?.grouped?.[libraryTab] || []
    if (!q) return recipes
    return recipes.filter(r =>
      r.name.toLowerCase().includes(q) ||
      (r.chef_notes || '').toLowerCase().includes(q)
    )
  }, [libraryTab, allDishes, recipeBrowser, searchQuery])

  // ── Workspace summary ──────────────────────────────────────────────────────
  const workspaceSummary = useMemo(() => {
    const filled = Object.values(workspace).filter(Boolean)
    const meals = filled.filter(m => !['broth', 'chia'].includes(m.category))
    const sides = filled.filter(m => ['broth', 'chia'].includes(m.category))
    const totalCal = filled.reduce((sum, m) => sum + (m.calories || 0), 0)
    const totalP = filled.reduce((sum, m) => sum + (m.protein || m.protein_g || 0), 0)
    const avgCal = meals.length > 0 ? Math.round(totalCal / meals.length) : 0
    const avgP = meals.length > 0 ? Math.round(totalP / meals.length) : 0
    return { meals: meals.length, sides: sides.length, totalCal, totalP, avgCal, avgP }
  }, [workspace])

  // ── Loading state ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-havn-green-600 border-t-transparent rounded-full mx-auto mb-3" />
          <p className="text-sm text-havn-gray-500">Loading meal builder...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">

      {/* ═══════════════════════════ WORKSPACE (TOP) ═══════════════════════════ */}
      <div className="bg-white rounded-2xl border border-havn-cream-300 shadow-sm overflow-hidden">
        {/* Workspace Header */}
        <div className="px-5 py-4 bg-havn-green-900 text-white flex items-center justify-between">
          <div>
            <h2 className="font-display font-semibold text-lg">Meal Builder Workspace</h2>
            <p className="text-sm text-white/60">
              {workspaceSummary.meals}/7 meals · {workspaceSummary.sides}/2 sides
              {workspaceSummary.avgCal > 0 && ` · Avg: ${workspaceSummary.avgCal} cal / ${workspaceSummary.avgP}g protein`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={saving || saved || Object.values(workspace).every(v => !v)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                saved
                  ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                  : 'bg-white/10 hover:bg-white/20 disabled:opacity-40'
              }`}
            >
              {saved ? (
                <>
                  <Check className="w-4 h-4" />
                  Saved!
                </>
              ) : saving ? (
                <>
                  <Save className="w-4 h-4 animate-pulse" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Draft
                </>
              )}
            </button>
          </div>
        </div>

        {/* Meal Slots */}
        <div className="p-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            {MEAL_SLOTS.map(slot => (
              <WorkspaceSlot
                key={slot.id}
                slot={slot}
                meal={workspace[slot.id]}
                isActive={activeSlot === slot.id}
                onClick={() => setActiveSlot(activeSlot === slot.id ? null : slot.id)}
                onClear={() => setWorkspace(prev => ({ ...prev, [slot.id]: null }))}
                isSide={false}
              />
            ))}
          </div>

          {/* Side Slots */}
          <div className="grid grid-cols-2 gap-3">
            {SIDE_SLOTS.map(slot => (
              <WorkspaceSlot
                key={slot.id}
                slot={slot}
                meal={workspace[slot.id]}
                isActive={activeSlot === slot.id}
                onClick={() => setActiveSlot(activeSlot === slot.id ? null : slot.id)}
                onClear={() => setWorkspace(prev => ({ ...prev, [slot.id]: null }))}
                isSide={true}
              />
            ))}
          </div>
        </div>

        {/* Active slot hint */}
        {activeSlot && (
          <div className="px-5 pb-4">
            <div className="flex items-center gap-2 px-3 py-2 bg-havn-green-50 border border-havn-green-200 rounded-lg text-xs text-havn-green-700">
              <ChevronDown className="w-4 h-4" />
              <span>
                <strong>{activeSlot.startsWith('side-') ? SIDE_SLOTS.find(s => s.id === activeSlot)?.label : MEAL_SLOTS.find(s => s.id === activeSlot)?.label}</strong> slot selected — pick items from the library below
              </span>
              <button onClick={() => setActiveSlot(null)} className="ml-auto text-havn-green-500 hover:text-havn-green-700">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ═══════════════════════════ LIBRARY (BOTTOM) ═══════════════════════════ */}
      <div className="bg-white rounded-2xl border border-havn-cream-300 shadow-sm overflow-hidden">
        {/* Library Tabs */}
        <div className="px-5 pt-4 pb-0 border-b border-havn-cream-200">
          <div className="flex items-center gap-1 overflow-x-auto pb-0 scrollbar-thin">
            {LIBRARY_TABS.map(tab => {
              const Icon = tab.icon
              const isActive = libraryTab === tab.id
              const count = tab.id === 'dishes'
                ? allDishes.length
                : recipeBrowser?.counts?.[tab.id] || 0
              return (
                <button
                  key={tab.id}
                  onClick={() => { setLibraryTab(tab.id); setSearchQuery('') }}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${
                    isActive
                      ? 'border-havn-green-600 text-havn-green-700'
                      : 'border-transparent text-havn-gray-500 hover:text-havn-charcoal hover:border-havn-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    isActive ? 'bg-havn-green-100 text-havn-green-700' : 'bg-havn-cream-200 text-havn-gray-500'
                  }`}>
                    {count}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Search */}
        <div className="px-5 pt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-havn-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={`Search ${LIBRARY_TABS.find(t => t.id === libraryTab)?.label.toLowerCase() || 'items'}...`}
              className="w-full pl-9 pr-8 py-2.5 bg-havn-cream-50 border border-havn-cream-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-havn-green-500 focus:border-transparent"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-havn-gray-400 hover:text-havn-charcoal">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Library Grid */}
        <div className="p-5">
          {filteredLibrary.length === 0 ? (
            <div className="text-center py-12">
              <Search className="w-10 h-10 mx-auto mb-3 text-havn-gray-300" />
              <p className="text-havn-gray-500">
                {libraryTab === 'dishes' && !searchQuery && allDishes.length === 0
                  ? 'No dishes available for this city'
                  : 'No items found'}
              </p>
              <p className="text-sm text-havn-gray-400 mt-1">
                {libraryTab === 'dishes' && !searchQuery && allDishes.length === 0
                  ? 'Switch to Washington DC to see the full dish library'
                  : 'Try a different search term'}
              </p>
            </div>
          ) : libraryTab === 'dishes' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {filteredLibrary.map(dish => (
                <DishCard
                  key={dish.id}
                  dish={dish}
                  onAdd={addDishToSlot}
                  onViewDetail={setDetailDish}
                />
              ))}
            </div>
          ) : libraryTab === 'bases' ? (
            <div>
              {(() => {
                const broths = filteredLibrary.filter(r => r.name.toLowerCase().includes('broth'))
                const chia = filteredLibrary.filter(r => !r.name.toLowerCase().includes('broth'))
                return (
                  <>
                    {broths.length > 0 && (
                      <>
                        <div className="flex items-center gap-2 mb-3">
                          <Soup className="w-4 h-4 text-orange-500" />
                          <h3 className="text-xs font-semibold text-havn-gray-500 uppercase tracking-wider">
                            Bone Broths
                          </h3>
                          <span className="text-xs text-havn-gray-400">({broths.length})</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-6">
                          {broths.map((recipe, i) => (
                            <RecipeCard
                              key={recipe.id || i}
                              recipe={recipe}
                              onAdd={addRecipeToSlot}
                              onViewDetail={setDetailRecipe}
                            />
                          ))}
                        </div>
                      </>
                    )}
                    {chia.length > 0 && (
                      <>
                        <div className="flex items-center gap-2 mb-3">
                          <CakeSlice className="w-4 h-4 text-pink-500" />
                          <h3 className="text-xs font-semibold text-havn-gray-500 uppercase tracking-wider">
                            Chia Puddings
                          </h3>
                          <span className="text-xs text-havn-gray-400">({chia.length})</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                          {chia.map((recipe, i) => (
                            <RecipeCard
                              key={recipe.id || i}
                              recipe={recipe}
                              onAdd={addRecipeToSlot}
                              onViewDetail={setDetailRecipe}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )
              })()}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {filteredLibrary.map((recipe, i) => (
                <RecipeCard
                  key={recipe.id || i}
                  recipe={recipe}
                  onAdd={addRecipeToSlot}
                  onViewDetail={setDetailRecipe}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ═══════════════════ DRAWERS ═══════════════════ */}
      {detailRecipe && (
        <RecipeDetailDrawer
          recipe={detailRecipe}
          onClose={() => setDetailRecipe(null)}
          onAdd={addRecipeToSlot}
        />
      )}
      {detailDish && (
        <DishDetailDrawer
          dish={detailDish}
          onClose={() => setDetailDish(null)}
          onAdd={addDishToSlot}
          swapOptions={swapOptions}
        />
      )}
    </div>
  )
}
